import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { api } from "@/lib/api-client"
import { queryKeys } from "@/lib/query-keys"

import { useAIParserSession } from "./use-ai-parser-session"

type AddTemplateData = {
  sessionId: string
  title: string
  type: "personal" | "company"
  templateId?: string
}

function addTemplate(data: AddTemplateData): Promise<void> {
  const { sessionId, title, type, templateId } = data

  return api.post("/parser/add-template", {
    session_id: sessionId,
    title,
    type,
    template_id: templateId,
  })
}

export function useAddTemplateMutation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { currentSessionId } = useAIParserSession()

  return useMutation({
    mutationFn: (data: { title: string; type: "personal" | "company"; templateId?: string }) => {
      if (!currentSessionId) {
        throw new Error("Session ID is required")
      }
      return addTemplate({
        sessionId: currentSessionId,
        title: data.title,
        type: data.type,
        templateId: data.templateId,
      })
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parserTemplates(),
      })

      const message = variables.templateId
        ? t("pages.ai_parser.toasts.template_updated_success")
        : t("pages.ai_parser.toasts.template_added_success")
      toast.success(message)
    },
    onError: () => {
      toast.error(t("pages.ai_parser.toasts.template_add_failed"))
    },
  })
}
